import { useEffect, useState, useMemo } from "react";
import { collection, getDocs, getFirestore, doc, updateDoc } from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import { FunnelIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const COLORS = [
  { label: "None", value: "" },
  { label: "Red", value: "red" },
  { label: "Blue", value: "blue" },
  { label: "Green", value: "green" },
  { label: "Yellow", value: "yellow" },
  { label: "Purple", value: "purple" },
];

const LEGACY_ID_MAP =
{
  1758106272292: "Name",
  1758106275875: "Email",
  1758106283582: "Contact",
  1758106316193: "Postal Code",
  1758106328216: "Dog's Name",
  1758106338259: "Dog's Breed",
  1758106343626: "Dog's Age",
  1758106349476: "Dog's Gender",
  1758106360226: "I am keen to attend the event on 27th Sept at 9am",
  1758106386826: "I am keen to attend the Practical Workshop: (Please complete the form below by Tuesday, 23rd September)",
  1758106408693: "How does your dog behave or respond when meeting other dogs and people?",
  1758106413810: "Is your dog receptive to food in an outdoor environment?",
  1758106420128: "Does your dog have a medical condition or currently on any medication?",
  1758106454061: "I have read and agree that my pet meets all of the following requirements:",
};


// Recursive component for question + sub-questions
const QuestionItem = ({ question, answers }) => {
  // inside QuestionItem
  let mainKey = `${question.id}_0`;
  let mainAnswer = answers[mainKey];

  // fallback: legacy ID map
  if (!mainAnswer) {
    const legacyEntry = Object.entries(LEGACY_ID_MAP).find(
      ([_, qText]) => qText === question.question
    );

    if (legacyEntry) {
      let [legacyKey] = legacyEntry;
      legacyKey = String(legacyKey); // ensure string key

      // add _0 if missing
      if (!answers[legacyKey] && !legacyKey.endsWith("_0")) {
        legacyKey = legacyKey + "_0";
      }

      let legacyAnswer = answers[legacyKey];
      // FORCE array -> string
      if (Array.isArray(legacyAnswer)) {
        mainAnswer = legacyAnswer.join(", "); // convert array to string
      } else if (legacyAnswer !== undefined && legacyAnswer !== null) {
        mainAnswer = String(legacyAnswer);
      }
    }
  }

  // fallback if nothing found
  if (!mainAnswer) mainAnswer = "-";


  return (
    <div className="mb-3">
      <p className="text-sm text-gray-600">{question.question}</p>
      <p className="font-medium ml-2">{mainAnswer}</p>

      {question.branches &&
        Object.values(question.branches).flat().map((subQ) => (
          <div key={subQ.id} className="ml-4">
            <QuestionItem question={subQ} answers={answers} />
          </div>
        ))}
    </div>
  );
};

const FormResponseModal = ({ open, setOpen, formId, editingForm }) => {
  const db = getFirestore(firebaseApp);
  const [responses, setResponses] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [filter, setFilter] = useState(""); // color filter
  const [searchText, setSearchText] = useState(""); // text search
  const [loading, setLoading] = useState(false);
  const [attendedFilter, setAttendedFilter] = useState(""); // "", "yes", "no"


  useEffect(() => {
    if (!open || !formId) return;

    const fetchResponses = async () => {
      try {
        setLoading(true);
        const responsesCol = collection(db, "forms", formId, "responses");
        const snap = await getDocs(responsesCol);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        data.sort((a, b) => b.submittedAt.seconds - a.submittedAt.seconds);
        setResponses(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching responses:", err);
        setLoading(false);
      }
    };
    fetchResponses();
  }, [open, formId]);

  const normalizeAnswer = (val) => {
    if (val === undefined || val === null) return "-";
    if (Array.isArray(val)) return val.join(", "); // handles legacy array
    return String(val); // handles string or number
  };

  const getAnswerForQuestion = (question, answers) => {
    const mainKey = `${question.id}_0`;
    let mainAnswer = answers[mainKey];

    if (!mainAnswer) {
      // Check legacy map
      const legacyEntry = Object.entries(LEGACY_ID_MAP).find(
        ([legacyKey, legacyQuestionText]) => legacyQuestionText === question.question
      );

      if (legacyEntry) {
        let [legacyKey] = legacyEntry;
        if (!answers[legacyKey] && !legacyKey.endsWith("_0")) {
          legacyKey = legacyKey + "_0";
        }
        mainAnswer = answers[legacyKey];
      }
    }

    return normalizeAnswer(mainAnswer);
  };

const buildExcelData = (responses, form) => {
  const allQuestions = [];
  form.groups.forEach((group) => {
    const addQuestionsRecursively = (qs) => {
      qs.forEach((q) => {
        allQuestions.push(q);
        if (q.branches) {
          Object.values(q.branches).flat().forEach(subQ => addQuestionsRecursively([subQ]));
        }
      });
    };
    addQuestionsRecursively(group.questions);
  });

  const data = responses.map((res) => {
    const row = {};
    allQuestions.forEach((q) => {
      row[q.question] = getAnswerForQuestion(q, res.answers);
    });

    // Add extra fields here
    row["Attended"] = res.attended ? "Yes" : "No"; // fallback if undefined = No
    row["Color"] = res.color || "-";
    row["Submitted At"] = formatDate(res.submittedAt);

    return row;
  });

  const headers = [
    ...allQuestions.map(q => q.question),
    "Attended",
    "Color",
    "Submitted At",
  ];

  return { data, headers };
};

  const exportToExcel = () => {
    if (!responses || responses.length === 0) return;

    const { data, headers } = buildExcelData(responses, editingForm);

    const ws = XLSX.utils.json_to_sheet(data, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Responses");
    XLSX.writeFile(wb, `form-${formId}-responses.xlsx`);
  };

  const formatDate = (ts) => {
    const date = new Date(ts.seconds * 1000);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const groupByMonth = (list) => {
    return list.reduce((acc, res) => {
      const d = new Date(res.submittedAt.seconds * 1000);
      const monthYear = d.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      if (!acc[monthYear]) acc[monthYear] = [];
      acc[monthYear].push(res);
      return acc;
    }, {});
  };

  const updateColor = async (resId, color) => {
    try {
      const ref = doc(db, "forms", formId, "responses", resId);
      await updateDoc(ref, { color });
      setResponses((prev) =>
        prev.map((r) => (r.id === resId ? { ...r, color } : r))
      );
      if (selectedResponse?.id === resId) {
        setSelectedResponse({ ...selectedResponse, color });
      }
    } catch (err) {
      console.error("Error updating color:", err);
    }
  };

  // -------------------
  // FILTER + SEARCH LOGIC
  // -------------------
  const filteredResponses = useMemo(() => {
  let res = responses;

  if (filter) res = res.filter((r) => r.color === filter);

  if (attendedFilter === "yes") res = res.filter((r) => r.attended);
  else if (attendedFilter === "no") res = res.filter((r) => !r.attended);

  if (searchText.trim()) {
    const lowerText = searchText.toLowerCase();
    res = res.filter((r) =>
      Object.values(r.answers).some((ans) =>
        String(ans).toLowerCase().includes(lowerText)
      )
    );
  }

  return res;
}, [responses, filter, searchText, attendedFilter]);

  if (!open) return null;

  const currentIndex = selectedResponse
    ? filteredResponses.findIndex((r) => r.id === selectedResponse.id)
    : -1;

  const goPrev = () => {
    if (currentIndex > 0)
      setSelectedResponse(filteredResponses[currentIndex - 1]);
  };
  const goNext = () => {
    if (currentIndex < filteredResponses.length - 1)
      setSelectedResponse(filteredResponses[currentIndex + 1]);
  };
  const toggleAttended = async (resId, current) => {
    try {
      const ref = doc(db, "forms", formId, "responses", resId);
      await updateDoc(ref, { attended: !current });
      setResponses((prev) =>
        prev.map((r) => (r.id === resId ? { ...r, attended: !current } : r))
      );
      if (selectedResponse?.id === resId) {
        setSelectedResponse({ ...selectedResponse, attended: !current });
      }
    } catch (err) {
      console.error("Error updating attended:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white w-full max-w-3xl mx-2 sm:mx-4 p-4 sm:p-6 rounded shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          {!selectedResponse ? (
            <h2 className="text-lg sm:text-2xl font-bold">Form Responses</h2>
          ) : (
            <button
              onClick={() => setSelectedResponse(null)}
              className="text-blue-500 text-sm sm:text-base"
            >
              ← Back
            </button>
          )}
          <button
            onClick={() => setOpen(false)}
            className="text-gray-500 hover:text-gray-700 text-base sm:text-lg"
          >
            ✕
          </button>
        </div>

        <div className="text-sm text-gray-600 mb-2">
  {responses.length > 0 && (
    <>
      {
        (() => {
          const attendedCount = responses.filter(r => r.attended).length;
          return `${attendedCount} out of ${responses.length} attended`;
        })()
      }
    </>
  )}
</div>

        {!selectedResponse && responses.length > 0 && (
          <div>
            {/* Filter */}
            {/* Filter Row */}
<div className="flex flex-wrap items-center justify-between gap-2 mb-2">
  <div className="flex items-center gap-2">
    <FunnelIcon className="h-5 w-5 text-gray-500" />
    <select
      value={filter}
      onChange={(e) => setFilter(e.target.value)}
      className="border rounded p-1 text-sm"
    >
      <option value="">All Colors</option>
      {COLORS.filter((c) => c.value).map((c) => (
        <option key={c.value} value={c.value}>
          {c.label}
        </option>
      ))}
    </select>

    <select
      value={attendedFilter}
      onChange={(e) => setAttendedFilter(e.target.value)}
      className="border rounded p-1 text-sm"
    >
      <option value="">All Attendance</option>
      <option value="yes">Attended</option>
      <option value="no">Not Attended</option>
    </select>
  </div>

  <button
    onClick={exportToExcel}
    className="bg-green-700 hover:bg-green-600 text-white text-sm sm:text-base px-3 py-1 rounded"
  >
    Export Data
  </button>
</div>


            {/* Search */}
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search responses..."
                className="flex-1 border rounded p-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {loading && <ArrowPathIcon className="w-5 h-5 text-gray-500 animate-spin" />}
            </div>

            {filteredResponses.length === 0 && (
              <p className="text-sm text-gray-500">No matching responses.</p>
            )}

            {Object.entries(groupByMonth(filteredResponses)).map(([month, subs]) => (
              <div key={month} className="mb-4">
                <h3 className="text-xs font-semibold text-gray-500 mb-2">{month.toUpperCase()}</h3>
                <div className="border rounded divide-y">
                  {subs.map((res) => {
                    // Preview: first question of first group
                    let previewAnswer = "-";
                    if (editingForm.groups?.[0]?.questions?.[0]) {
                      const firstQ = editingForm.groups[0].questions[0];
                      previewAnswer = getAnswerForQuestion(firstQ, res.answers);
                    }

                    return (
                      <div
                        key={res.id}
                        className="flex flex-col justify-between p-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedResponse(res)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            {formatDate(res.submittedAt)}
                            {res.color && (
                              <span
                                className={`ml-2 inline-block w-3 h-3 rounded-full bg-${res.color}-500`}
                              ></span>
                            )}
                          </div>
                          <select
                            value={res.color || ""}
                            onChange={(e) => updateColor(res.id, e.target.value)}
                            className="border rounded p-1 text-xs"
                          >
                            {COLORS.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Preview Answer */}
                        <p className="text-sm text-gray-600 mt-1 truncate">{previewAnswer}</p>
                        <span
  className={`mt-1 inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${
    res.attended
      ? "bg-green-100 text-green-700"
      : "bg-red-100 text-red-700"
  }`}
>
  {res.attended ? "Attended" : "Not Attended"}
</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DETAIL VIEW */}
        {selectedResponse && (
          
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">{formatDate(selectedResponse.submittedAt)}</h2>
              <select
                value={selectedResponse.color || ""}
                onChange={(e) => updateColor(selectedResponse.id, e.target.value)}
                className="border rounded p-1 text-sm"
              >
                {COLORS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            {editingForm.groups.map((group) => (
              <div key={group.id} className="mb-6">
                {group.name && <h3 className="font-medium mb-2">{group.name}</h3>}
                {group.questions.map((q) => (
                  <QuestionItem key={q.id} question={q} answers={selectedResponse.answers} />
                ))}
              </div>
            ))}
            <div className="mb-4 flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm sm:text-base">
                <input
                  type="checkbox"
                  checked={selectedResponse.attended || false}
                  onChange={() =>
                    toggleAttended(selectedResponse.id, selectedResponse.attended)
                  }
                  className="w-4 h-4"
                />
                Attended
              </label>
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              <button
                onClick={goPrev}
                disabled={currentIndex === 0}
                className={`px-4 py-2 rounded ${currentIndex === 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
              >
                Prev
              </button>
              <button
                onClick={goNext}
                disabled={currentIndex === filteredResponses.length - 1}
                className={`px-4 py-2 rounded ${currentIndex === filteredResponses.length - 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormResponseModal;
